import { Directive, EventEmitter, forwardRef, InjectionToken, Input } from '@angular/core';
import {
  GridRowGroup,
  isGridRowGroup,
} from '@assetaccountant/core/components/grid/grid-table/grid-table.component';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  GetRowDataParams,
  GetRowDataResult,
  GRID_ROW_DATA,
  GridRowDataProvider,
} from './grid-row-data.provider';

export const ROW_DATA = new InjectionToken<RowDataProvider<any>>('ROW_DATA');

export interface RowDataProvider<T> {
  readonly rowData$: BehaviorSubject<readonly T[] | null>;
}

@Directive({
  selector: 'app-grid-table[rowData]',
  providers: [
    { provide: ROW_DATA, useExisting: forwardRef(() => GridRowDataDirective) },
    { provide: GRID_ROW_DATA, useExisting: forwardRef(() => GridRowDataDirective) },
  ],
  standalone: false,
})
export class GridRowDataDirective<T extends {}>
  implements GridRowDataProvider<T>, RowDataProvider<T>
{
  @Input() set rowData(value: readonly T[] | null | undefined) {
    this.rowData$.next(value ?? null);
    this.refresh.emit({ purge: value != null });
  }

  @Input() isRowGroupOpenByDefault?: (params: { field: string; key: any }) => boolean;

  readonly infiniteScroll = false;

  rowData$ = new BehaviorSubject<readonly T[] | null>(null);

  refresh = new EventEmitter<{ purge: boolean }>();

  getRowData({ top, parent, columns }: GetRowDataParams<T>): Observable<GetRowDataResult<T>> {
    if (top > 0 || parent != null) {
      throw new Error('Unexpected request');
    }

    const rowGroupColumns = columns.filter(c => c.rowGroup);

    if (rowGroupColumns.length === 0) {
      return of({
        rows:
          this.rowData$.value?.map((data, i) => ({
            groupIndex: i,
            data,
            state: 'LOADED' as const,
          })) ?? [],
        count: this.rowData$?.value?.length ?? null,
      });
    }

    const getRowGroupKey = row =>
      rowGroupColumns.map(c => (c.valueGetter(row) as any)?.toString() ?? '');

    const initialGroup: GridRowGroup<T> & Required<Pick<GridRowGroup<T>, 'children'>> = {
      key: '',
      children: [],
      open: true,
      count: 0,
    };
    this.rowData$.value?.forEach(row => {
      const rowGroupKeys = getRowGroupKey(row) ?? [];

      let currentGroup: GridRowGroup<T> = initialGroup;
      rowGroupKeys.forEach((k, i) => {
        let rowGroup = currentGroup.children?.filter(isGridRowGroup).find(r => r.key === k);
        if (!rowGroup) {
          rowGroup = {
            key: k,
            children: [],
            open: this.isRowGroupOpenByDefault
              ? this.isRowGroupOpenByDefault({ field: rowGroupColumns[i].field as string, key: k })
              : true,
            count: 0,
          };
          currentGroup.children = [...(currentGroup.children ?? []), rowGroup];
        }
        currentGroup = rowGroup;
        currentGroup.count = (currentGroup.count ?? 0) + 1;
      });

      currentGroup.children = [
        ...(currentGroup.children ?? []),
        {
          groupKey: currentGroup.key,
          groupIndex: currentGroup.children?.length ?? 0,
          data: row,
          state: 'LOADED' as const,
        },
      ];
    });

    const result = initialGroup.children;

    return of({
      rows: result,
      count: result.length,
    });
  }
}
